import React, { useState, useCallback } from 'react';
import {
  FiX, FiUploadCloud, FiCheckCircle, FiAlertTriangle, FiAlertCircle,
  FiInfo, FiArrowRight, FiMap, FiChevronDown, FiChevronRight,
  FiCpu, FiDatabase, FiGlobe,
} from 'react-icons/fi';
import {
  analyzeMigration, saveAiCache,
  type MigrationReport, type MigrationPhase,
} from '../../api/ai';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  provider: string;
  environment: string;
  architectureName?: string;
  architectureId?: string;
  currentNodes?: any[];
  onApplyNodes?: (nodes: any[], edges: any[]) => void;
}

const COMPLEXITY_CONFIG = {
  low:    { label: 'Faible',  badge: 'bg-green-100 text-green-700 border-green-300' },
  medium: { label: 'Moyen',   badge: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
  high:   { label: 'Élevé',   badge: 'bg-red-100 text-red-700 border-red-300' },
};

const EXAMPLE_INPUTS = [
  {
    label: 'VM + DB on-prem',
    value: `Infrastructure existante on-premise:
- 2x serveurs web Apache (Linux, 8 CPU, 32GB RAM)
- 1x base de données MySQL 8.0 (16 CPU, 64GB RAM, 2TB SSD)
- 1x serveur Redis pour le cache (8GB RAM)
- Load balancer HAProxy
- Firewall pfSense
- Stockage NFS partagé 10TB`,
  },
  {
    label: 'AWS vers Azure',
    value: `Infrastructure AWS actuelle:
- 3x EC2 t3.large (Auto Scaling Group)
- RDS PostgreSQL db.t3.medium (Multi-AZ)
- ElastiCache Redis cluster
- Application Load Balancer
- S3 bucket pour les assets (500GB)
- CloudFront CDN
- Route 53 DNS`,
  },
  {
    label: 'Terraform snippet',
    value: `resource "aws_instance" "web" {
  ami           = "ami-0c55b159cbfafe1f0"
  instance_type = "t3.medium"
  count         = 2
}
resource "aws_db_instance" "db" {
  engine         = "postgres"
  instance_class = "db.t3.small"
  allocated_storage = 100
}
resource "aws_s3_bucket" "assets" {
  bucket = "my-app-assets"
}`,
  },
];

function PhaseCard({ phase, defaultOpen }: { phase: MigrationPhase; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen || false);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left">
        <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center shrink-0">
          {phase.phase}
        </span>
        <span className="flex-1 text-sm font-medium text-gray-800">{phase.title}</span>
        <span className="text-xs text-gray-500 mr-2">{phase.duration}</span>
        {open ? <FiChevronDown size={14} className="text-gray-400 shrink-0" /> : <FiChevronRight size={14} className="text-gray-400 shrink-0" />}
      </button>
      {open && (
        <div className="px-4 py-3 space-y-3">
          {phase.tasks.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-1.5">Tâches</p>
              <ul className="space-y-1">
                {phase.tasks.map((t, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-gray-700">
                    <FiArrowRight size={11} className="text-indigo-400 mt-0.5 shrink-0" />{t}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {phase.deliverables.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-1.5">Livrables</p>
              <ul className="space-y-1">
                {phase.deliverables.map((d, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-gray-700">
                    <FiCheckCircle size={11} className="text-green-500 mt-0.5 shrink-0" />{d}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {phase.risks.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-1.5">Risques</p>
              <ul className="space-y-1">
                {phase.risks.map((r, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-xs text-orange-700">
                    <FiAlertTriangle size={11} className="mt-0.5 shrink-0" />{r}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const MigrationAssistantModal: React.FC<Props> = ({
  isOpen, onClose, provider, environment, architectureName,
  architectureId, currentNodes, onApplyNodes,
}) => {
  const [step, setStep]             = useState<'input' | 'result'>('input');
  const [infraInput, setInfraInput] = useState('');
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [report, setReport]         = useState<MigrationReport | null>(null);
  const [metadata, setMetadata]     = useState<{ durationMs: number; tokensUsed: number } | null>(null);
  const [activeTab, setActiveTab]   = useState<'plan' | 'mapping' | 'diagram'>('plan');
  const [applied, setApplied]       = useState(false);

  const providerName = { azure: 'Azure', aws: 'AWS', gcp: 'Google Cloud' }[provider] || provider;

  const handleAnalyze = async () => {
    if (!infraInput.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const result = await analyzeMigration({
        existingInfra: infraInput,
        provider,
        environment,
        targetArchitectureName: architectureName,
        currentNodes: currentNodes || [],
      });
      setReport(result.report);
      setMetadata({ durationMs: result.metadata.durationMs, tokensUsed: result.metadata.tokensUsed });
      if (architectureId) {
        saveAiCache(architectureId, 'migration', { report: result.report, metadata: result.metadata }).catch(() => {});
      }
      setStep('result');
      setActiveTab('plan');
      setApplied(false);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erreur lors de l\'analyse');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyNodes = useCallback(() => {
    if (!report || !onApplyNodes) return;
    onApplyNodes(report.suggestedNodes || [], report.suggestedEdges || []);
    setApplied(true);
  }, [report, onApplyNodes]);

  const handleExportPdf = () => {
    if (!report) return;
    const w = window.open('', '_blank');
    if (!w) return;
    const phasesHtml = report.migrationPlan.phases.map(p => `
      <div class="phase">
        <h4>Phase ${p.phase} — ${p.title} <span class="duration">${p.duration}</span></h4>
        <b>Tâches :</b><ul>${p.tasks.map(t => `<li>${t}</li>`).join('')}</ul>
        ${p.deliverables.length ? `<b>Livrables :</b><ul>${p.deliverables.map(d => `<li>${d}</li>`).join('')}</ul>` : ''}
        ${p.risks.length ? `<b>Risques :</b><ul class="risks">${p.risks.map(r => `<li>${r}</li>`).join('')}</ul>` : ''}
      </div>`).join('');
    const mappingHtml = report.geniacMapping.map(m => `
      <tr><td>${m.existing}</td><td>${m.geniacType}</td><td>${m.equivalentService}</td><td>${m.migrationNotes}</td></tr>`).join('');
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Plan de Migration — ${architectureName || providerName}</title>
    <style>
      body{font-family:system-ui,sans-serif;padding:32px;max-width:900px;margin:0 auto;color:#111}
      h1{font-size:20px;font-weight:700}h2{font-size:16px;font-weight:600;margin:20px 0 8px;border-bottom:1px solid #e5e7eb;padding-bottom:4px}
      h3{font-size:14px;font-weight:600;margin:14px 0 6px}h4{font-size:13px;font-weight:600;margin:10px 0 4px}
      .phase{border:1px solid #e5e7eb;border-radius:8px;padding:12px 16px;margin:8px 0}
      .duration{font-weight:400;color:#6b7280;font-size:12px;margin-left:8px}
      ul{margin:4px 0;padding-left:20px}li{margin:2px 0;font-size:13px}
      .risks li{color:#b45309}.badge{display:inline-block;padding:2px 8px;border-radius:9999px;font-size:11px;font-weight:600}
      table{width:100%;border-collapse:collapse;margin:12px 0}
      th{background:#f3f4f6;text-align:left;padding:8px 12px;font-size:12px}
      td{padding:8px 12px;font-size:12px;border-bottom:1px solid #e5e7eb}
      @media print{body{padding:16px}}
    </style></head><body>`);
    w.document.write(`<h1>Plan de Migration vers ${providerName}</h1>`);
    w.document.write(`<p><b>Architecture :</b> ${architectureName || 'N/A'} | <b>Env :</b> ${environment} | <b>Complexité :</b> ${report.analysis.complexity} | <b>Effort estimé :</b> ${report.analysis.estimatedEffort}</p>`);
    w.document.write(`<p>${report.analysis.summary}</p>`);
    if (report.analysis.mainChallenges?.length) {
      w.document.write(`<h2>Défis principaux</h2><ul>${report.analysis.mainChallenges.map(c => `<li>${c}</li>`).join('')}</ul>`);
    }
    w.document.write(`<h2>Plan de migration (${report.migrationPlan.phases.length} phases)</h2>${phasesHtml}`);
    if (report.migrationPlan.prerequisites?.length) {
      w.document.write(`<h2>Prérequis</h2><ul>${report.migrationPlan.prerequisites.map(p => `<li>${p}</li>`).join('')}</ul>`);
    }
    w.document.write(`<p><b>Stratégie de rollback :</b> ${report.migrationPlan.rollbackStrategy}</p>`);
    w.document.write(`<p><b>Approche de test :</b> ${report.migrationPlan.testingApproach}</p>`);
    if (report.estimatedMonthlyCost) {
      w.document.write(`<h2>Coûts</h2><p><b>Coût mensuel estimé :</b> ${report.estimatedMonthlyCost}</p><p>${report.costComparison}</p>`);
    }
    if (mappingHtml) {
      w.document.write(`<h2>Correspondance des ressources</h2>
        <table><thead><tr><th>Existant</th><th>Type GenIaC</th><th>Service ${providerName}</th><th>Notes</th></tr></thead>
        <tbody>${mappingHtml}</tbody></table>`);
    }
    w.document.write('</body></html>');
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  if (!isOpen) return null;

  const complexityCfg = report ? (COMPLEXITY_CONFIG[report.analysis.complexity] || COMPLEXITY_CONFIG.medium) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <FiUploadCloud className="text-teal-600" size={20} />
            <h2 className="text-lg font-semibold text-gray-800">Assistant Migration</h2>
            <span className="text-xs text-gray-400 font-normal">vers {providerName}</span>
          </div>
          <div className="flex items-center gap-2">
            {step === 'result' && (
              <button onClick={() => { setStep('input'); setReport(null); setError(null); }}
                className="text-xs text-gray-500 hover:text-teal-600 transition-colors px-2 py-1 rounded hover:bg-gray-50">
                ← Nouvelle analyse
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
              <FiX size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* ── Step 1: Input ── */}
          {step === 'input' && (
            <div className="space-y-5">
              <div>
                <p className="text-sm text-gray-600 mb-1">
                  Décrivez votre infrastructure existante ou collez un extrait Terraform / CloudFormation / ARM / YAML.
                  L'IA analysera votre infra et générera un plan de migration vers <strong>{providerName}</strong>.
                </p>
              </div>

              {/* Example buttons */}
              <div className="flex flex-wrap gap-2">
                <span className="text-xs text-gray-500 self-center">Exemples :</span>
                {EXAMPLE_INPUTS.map(ex => (
                  <button key={ex.label}
                    onClick={() => setInfraInput(ex.value)}
                    className="text-xs px-2.5 py-1 rounded-full border border-gray-200 hover:border-teal-400 hover:text-teal-700 text-gray-600 transition-colors">
                    {ex.label}
                  </button>
                ))}
              </div>

              <textarea
                className="w-full h-56 border border-gray-200 rounded-xl p-4 text-sm font-mono focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400 resize-none placeholder-gray-400"
                placeholder={`# Décrivez votre infrastructure existante
# Exemples acceptés:
# - Description textuelle ("2 serveurs web, 1 base MySQL...")
# - Terraform HCL
# - CloudFormation YAML/JSON
# - ARM Templates JSON
# - Description d'une infra AWS/Azure/GCP/on-prem`}
                value={infraInput}
                onChange={e => setInfraInput(e.target.value)}
              />

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm flex items-center gap-2">
                  <FiAlertCircle size={14} />{error}
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={handleAnalyze}
                  disabled={loading || !infraInput.trim()}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-40"
                  style={{ background: loading || !infraInput.trim() ? '#9ca3af' : 'linear-gradient(135deg, #0d9488, #0891b2)' }}>
                  {loading
                    ? <><FiUploadCloud size={15} className="animate-bounce" /> Analyse en cours…</>
                    : <><FiUploadCloud size={15} /> Analyser et planifier la migration</>}
                </button>
              </div>
            </div>
          )}

          {/* ── Step 2: Results ── */}
          {step === 'result' && report && (
            <div className="space-y-5">

              {/* Summary card */}
              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-4 flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <p className="text-sm text-gray-700 mb-2">{report.analysis.summary}</p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="px-2 py-0.5 rounded-full bg-white border border-teal-200 text-teal-700 font-medium">
                      Source : {report.analysis.sourceProvider}
                    </span>
                    {complexityCfg && (
                      <span className={`px-2 py-0.5 rounded-full border font-medium ${complexityCfg.badge}`}>
                        Complexité : {complexityCfg.label}
                      </span>
                    )}
                    <span className="px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-600">
                      Effort : {report.analysis.estimatedEffort}
                    </span>
                    {report.estimatedMonthlyCost && (
                      <span className="px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-600">
                        ~{report.estimatedMonthlyCost}
                      </span>
                    )}
                  </div>
                </div>
                {report.analysis.mainChallenges?.length > 0 && (
                  <div className="border-l border-teal-100 pl-4 min-w-[180px]">
                    <p className="text-xs font-semibold text-gray-600 mb-1.5">Défis principaux</p>
                    {report.analysis.mainChallenges.map((c, i) => (
                      <div key={i} className="flex items-start gap-1.5 mb-1">
                        <FiAlertTriangle size={11} className="text-orange-400 mt-0.5 shrink-0" />
                        <span className="text-xs text-gray-600">{c}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Tabs */}
              <div className="flex gap-1 border-b border-gray-200">
                {([
                  { key: 'plan',    label: `Plan (${report.migrationPlan.phases.length} phases)`, icon: <FiMap size={13} /> },
                  { key: 'mapping', label: `Correspondances (${report.geniacMapping.length})`,     icon: <FiArrowRight size={13} /> },
                  { key: 'diagram', label: `Diagram (${report.suggestedNodes.length} nodes)`,      icon: <FiCpu size={13} /> },
                ] as const).map(tab => (
                  <button key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex items-center gap-1.5 text-xs font-medium px-3 py-2.5 border-b-2 transition-colors ${
                      activeTab === tab.key
                        ? 'border-teal-500 text-teal-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}>
                    {tab.icon}{tab.label}
                  </button>
                ))}
              </div>

              {/* Plan tab */}
              {activeTab === 'plan' && (
                <div className="space-y-3">
                  {report.migrationPlan.prerequisites?.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                      <p className="text-xs font-semibold text-blue-700 mb-1.5 flex items-center gap-1.5">
                        <FiInfo size={13} /> Prérequis
                      </p>
                      <ul className="space-y-1">
                        {report.migrationPlan.prerequisites.map((p, i) => (
                          <li key={i} className="text-xs text-blue-700 flex items-start gap-1.5">
                            <FiCheckCircle size={11} className="mt-0.5 shrink-0" />{p}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {report.migrationPlan.phases.map((phase, i) => (
                    <PhaseCard key={i} phase={phase} defaultOpen={i === 0} />
                  ))}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                    {report.migrationPlan.rollbackStrategy && (
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                        <p className="text-xs font-semibold text-orange-700 mb-1">Rollback</p>
                        <p className="text-xs text-orange-700">{report.migrationPlan.rollbackStrategy}</p>
                      </div>
                    )}
                    {report.migrationPlan.testingApproach && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                        <p className="text-xs font-semibold text-green-700 mb-1">Tests</p>
                        <p className="text-xs text-green-700">{report.migrationPlan.testingApproach}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Mapping tab */}
              {activeTab === 'mapping' && (
                <div className="space-y-2">
                  {report.geniacMapping.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-8">Aucune correspondance générée.</p>
                  )}
                  {report.geniacMapping.map((m, i) => (
                    <div key={i} className="border border-gray-200 rounded-lg p-3 flex flex-col sm:flex-row gap-3">
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        <FiDatabase size={13} className="text-gray-400 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-xs font-semibold text-gray-700">{m.existing}</p>
                          <p className="text-[10px] text-gray-500 mt-0.5">{m.migrationNotes}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 text-gray-300 shrink-0">
                        <FiArrowRight size={14} />
                      </div>
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        <FiGlobe size={13} className="text-teal-500 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-xs font-semibold text-teal-700">{m.equivalentService}</p>
                          <p className="text-[10px] font-mono text-gray-500 mt-0.5">{m.geniacType}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Diagram tab */}
              {activeTab === 'diagram' && (
                <div className="space-y-3">
                  {report.suggestedNodes.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-8">Aucun node suggéré.</p>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {report.suggestedNodes.map((node, i) => (
                      <div key={i} className="border border-gray-200 rounded-lg p-2.5 flex items-center gap-2">
                        <FiCpu size={14} className="text-teal-500 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-gray-800 truncate">{node.data?.label || node.id}</p>
                          <p className="text-[10px] font-mono text-gray-500">{node.data?.type}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  {report.suggestedNodes.length > 0 && onApplyNodes && (
                    <div className="pt-2">
                      {applied ? (
                        <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
                          <FiCheckCircle size={16} />
                          <span className="text-sm font-medium">Nodes ajoutés au canvas !</span>
                        </div>
                      ) : (
                        <button
                          onClick={handleApplyNodes}
                          className="w-full py-2.5 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 transition-all hover:opacity-90"
                          style={{ background: 'linear-gradient(135deg, #0d9488, #0891b2)' }}>
                          <FiCpu size={15} />
                          Ajouter {report.suggestedNodes.length} node{report.suggestedNodes.length !== 1 ? 's' : ''} au canvas
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

            </div>
          )}
        </div>

        {/* Footer */}
        {step === 'result' && report && (
          <div className="border-t border-gray-100 px-6 py-3 flex items-center justify-between gap-4 bg-gray-50 rounded-b-2xl">
            <div className="text-xs text-gray-400 flex items-center gap-3">
              {metadata && (
                <>
                  <span>{metadata.tokensUsed.toLocaleString()} tokens</span>
                  <span>{(metadata.durationMs / 1000).toFixed(1)}s</span>
                </>
              )}
            </div>
            <button
              onClick={handleExportPdf}
              className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-teal-600 transition-colors px-2.5 py-1.5 rounded-lg border border-gray-200 hover:border-teal-300">
              Export PDF
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MigrationAssistantModal;
